import { Link } from "wouter";
import { getNextStepsForTool } from "@/lib/workflows-data";
import { getToolById } from "@/lib/tools-data";
import { ArrowRight, Route } from "lucide-react";

/**
 * Shown on a tool page when that tool is the first (or a middle) step of
 * one of the curated workflows -- surfaces the actual next tool in a real
 * sequence, not a generic "you might also like" list. No automatic file
 * hand-off; this is discovery, same manual download/re-upload as the rest
 * of the site (see the corporate-safe / multi-tool-project discussion for
 * why that's a deliberate, separate decision).
 */
export function NextStepSuggestion({ toolId }: { toolId: string }) {
  const suggestions = getNextStepsForTool(toolId);
  if (suggestions.length === 0) return null;

  // Show at most one -- if a tool appears in multiple workflows, the first
  // one written is treated as the primary use case for it.
  const { workflow, nextStep } = suggestions[0];
  const nextTool = getToolById(nextStep.toolId);
  if (!nextTool) return null;

  return (
    <div className="mt-6 p-4 rounded-xl border border-primary/20 bg-primary/5">
      <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide mb-2">
        <Route className="h-3.5 w-3.5" />
        Part of: {workflow.title}
      </div>
      <Link
        href={`/tool/${nextTool.id}`}
        className="flex items-center justify-between group"
      >
        <div>
          <p className="text-sm font-medium">Next: {nextTool.name}</p>
          <p className="text-xs text-muted-foreground">{nextStep.note}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform shrink-0" />
      </Link>
    </div>
  );
}
